import React from 'react';
import {
  MdOutlineListAlt,
  MdSettings,
  MdViewInAr,
  MdWaterDrop,
} from 'react-icons/md';
import PropTypes from 'prop-types';
import { components } from 'react-select';
import styled, { useTheme } from 'styled-components';
import { BsImage } from 'react-icons/bs';

const OptionIcons = styled.div`
  color: ${({ theme }) => theme.COLOR_GREY_TEXT};
  margin-right: 10px;
`;

const OptionContainer = styled.div`
  display: flex;
  margin-left: -5px;
  display: flex;
  align-items: center;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0px 0 10px;
`;

export const IconOptions = [
  { value: 'MdOutlineListAlt', label: 'Spec' },
  { value: 'MdSettings', label: 'OEM' },
  { value: 'MdViewInAr', label: 'Model' },
  { value: 'MdWaterDrop', label: 'Capacity' },
];

/**
 * Icon to display
 * @param {string} icon - Icon to display
 * @returns {JSX.Element} - Returns a JSX element
 */
export const IconResolver = (icon) => {
  const theme = useTheme();

  switch (icon) {
    case 'MdOutlineListAlt':
      return (
        <IconContainer>
          <MdOutlineListAlt color={theme.COLOR_BLACK} />
        </IconContainer>
      );
    case 'MdSettings':
      return (
        <IconContainer>
          <MdSettings color={theme.COLOR_BLACK} />
        </IconContainer>
      );
    case 'MdViewInAr':
      return (
        <IconContainer>
          <MdViewInAr color={theme.COLOR_BLACK} />
        </IconContainer>
      );
    case 'MdWaterDrop':
      return (
        <IconContainer>
          <MdWaterDrop color={theme.COLOR_BLACK} />
        </IconContainer>
      );
    default:
      return (
        <IconContainer>
          <BsImage color={theme.COLOR_GREY4} />
        </IconContainer>
      );
  }
};

/**
 * Renders the icon option
 * @param {object} props - The props
 * @param {Function} props.getStyles - The styles
 * @param {boolean} props.isDisabled - Is disabled
 * @param {boolean} props.isFocused - Is focused
 * @param {boolean} props.isSelected - Is selected
 * @param {string} props.children - The children
 * @param {object} props.innerProps - The inner props
 * @returns {JSX.Element} - Returns a JSX element
 */
export const RenderIconOption = ({
  getStyles,
  isDisabled,
  isFocused,
  isSelected,
  children,
  innerProps,
  ...rest
}) => {
  const props = {
    ...innerProps,
  };

  return (
    <components.Option
      {...rest}
      isDisabled={isDisabled}
      isFocused={isFocused}
      isSelected={isSelected}
      getStyles={getStyles}
      innerProps={props}
    >
      <OptionContainer>
        <OptionIcons>{IconResolver(rest.value)}</OptionIcons>
        {children}
      </OptionContainer>
    </components.Option>
  );
};

RenderIconOption.defaultProps = {
  getStyles: undefined,
  isDisabled: false,
  isFocused: false,
  isSelected: false,
  children: undefined,
  innerProps: undefined,
};

RenderIconOption.propTypes = {
  getStyles: PropTypes.func,
  isDisabled: PropTypes.bool,
  isFocused: PropTypes.bool,
  isSelected: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  innerProps: PropTypes.shape({}),
};

/**
 * Renders the icon control
 * @param {object} props - The props
 * @param {object} props.children - The children
 * @returns {JSX.Element} - Returns a JSX element
 */
export const RenderIconControl = ({ children, ...props }) => {
  const selectedValue = props?.selectProps?.value?.value;
  return (
    <components.Control {...props}>
      {IconResolver(selectedValue)}
      {children}
    </components.Control>
  );
};

RenderIconControl.propTypes = {
  children: PropTypes.node,
  selectProps: PropTypes.shape({
    value: PropTypes.any,
  }),
};

RenderIconControl.defaultProps = {
  getStyles: undefined,
  isDisabled: false,
  isFocused: false,
  isSelected: false,
  children: undefined,
  innerProps: undefined,
  selectProps: undefined,
};

RenderIconControl.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  getStyles: PropTypes.func,
  innerProps: PropTypes.shape({}),
  isDisabled: PropTypes.bool,
  isFocused: PropTypes.bool,
  isSelected: PropTypes.bool,
};

export default { IconResolver, IconOptions };
